import React, { Component } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { get } from 'lodash'
import Shell from '../EditorTools/Shell'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import { wpAxios } from '../../../../config/axiosResolver'
import { pairBlocks, sides } from './diff'
import ConfigResolver from '../../../../config/ConfigResolver'

// Empty REACT_APP_SITE_URL makes these relative, so a local build talks to
// the API on its own origin instead of production. Defaults to the prod host,
// so nothing changes when it is not set.
const config = ConfigResolver.config()

// Standalone this panel IS the dialog and sizes itself. Embedded in the editor
// workspace it is a flex child of a panel that has already been sized, so it
// has to fill that rather than restate a width of its own -- 1240px inside an
// 860px workspace simply ran off the side of the modal.
const Panel = styled.div`
  max-width: ${p => p.$embedded ? 'none' : '92vw'};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  ${p => p.$embedded
    ? `
      flex: 1;
      min-height: 0;
      padding: 18px 30px 22px;
    `
    : `
      width: 1240px;
      height: 620px;
      max-height: 86vh;
      padding: 30px 34px;
    `}
`

const Title = styled.div`
  font-family: Hind;
  font-size: 20px;
  margin-bottom: 4px;
`

const Sub = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-weight: 300;
  color: #777;
  margin-bottom: 18px;
`

const Body = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
`

const List = styled.div`
  width: 260px;
  overflow-y: auto;
  border-right: 1px solid #EEE;
  padding-right: 14px;
`

const Diff = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-left: 20px;
`

const Row = styled.div`
  font-family: Hind;
  font-size: 14px;
  padding: 9px 10px;
  margin-bottom: 4px;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid ${p => p.$selected ? '#ECB595' : 'transparent'};
  background-color: ${p => p.$selected ? '#FDF6F1' : '#FAFAFA'};
`

const RowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`

const Who = styled.span`
  font-weight: ${p => p.$kind === 'editor' ? 500 : 300};
`

const Tag = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #FFF;
  /* Neutral, not green: this badge marks WHICH revision is the vendor's,
     which is independent of whether it is currently the before or after side.
     A near-sage green here collided with the "after" label colour and implied
     a relationship that does not exist. */
  background-color: ${p => p.$tone === 'submitted' ? '#8C8681' : '#B08BC0'};
  border-radius: 2px;
  padding: 1px 6px;
  margin-left: 6px;
`

const When = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: #999;
`

const Field = styled.div`
  margin-top: 20px;
`

// The two columns are a grid rather than flex so every field's halves stay on
// the same baseline. With flex, a long A paragraph beside a short B one lets
// the next field start at different heights on each side and the eye loses the
// row it was tracking.
const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 26px;
  align-items: start;
`

// Everything pinned to the top lives in one sticky band. Two reasons.
//
// Spacing has to be padding, not margin: margin sits OUTSIDE the background, so
// a margin-bottom leaves a transparent strip that scrolling text shows through
// — field titles were being sliced in half under the header rather than hidden
// by it.
//
// And the toggles belong here rather than scrolling away. Once you have scrolled
// into a long field, "read B without markup" is exactly when you want it, and
// having to scroll back up to reach it defeats the control.
const StickyTop = styled.div`
  position: sticky;
  top: 0;
  background: #FFF;
  padding-bottom: 12px;
  border-bottom: 1px solid #EEE;
  z-index: 2;
`

const ColumnHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 26px;
`

// "Before" and "After" rather than "A" and "B". A and B are arbitrary tokens the
// reader has to hold in their head and map back to a direction; before/after is
// the direction itself, and it is the language editors already use for a
// revision. It also removes the question the letters invited — which one is
// newer — that the ordering fix had to answer separately.
const ColumnLabel = styled.div`
  font-family: Hind;
  font-size: 13px;
  color: #666;
  /* Same coral/sage as the before/after marks in the revision list. The two
     were saying the same thing in different visual languages, which makes the
     reader re-learn the mapping in each place they look. */
  strong {
    font-weight: 500;
    color: ${p => p.$side === 'before' ? '#B4776F' : '#5F8A66'};
    text-transform: uppercase;
    letter-spacing: .05em;
    font-size: 11px;
  }
`

const SideMark = styled.div`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: ${p => p.$side === 'before' ? '#B4776F' : '#5F8A66'};
`

// Structure is neutral on purpose. The coloured column header states which side
// you are on, and the highlights state what changed; tinting the container as
// well said the same thing a third time. Counted in the browser, the panel had
// five competing coral/green families — with everything tinted, nothing reads as
// emphasis, and the diff highlights are the only thing that should.
const Cell = styled.div`
  border-left: 2px solid #EDEAE8;
  padding-left: 12px;
`

const FieldName = styled.div`
  font-family: Hind;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #A09A96;
  margin-bottom: 6px;
`

const Prose = styled.div`
  font-family: Hind;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.55;
  white-space: pre-wrap;
`

// Insertions and deletions are separated visually rather than relying on the
// text having a space at the boundary, because sometimes it cannot. A word at
// the end of a field carries no trailing whitespace, so a deletion that runs to
// the end of the old text butts straight against the insertion that replaces
// it — "details!!A", "perfect!!abundant". Mid-string boundaries do have a space,
// but it sits inside the highlight and struck through, which reads as no gap.
//
// Padding plus a margin guarantees the separation in every case and leaves the
// diff data untouched, so the text still reconstructs byte for byte.
const Ins = styled.span`
  background-color: #DFF0DC;
  text-decoration: none;
  border-radius: 2px;
  padding: 1px 3px;
  margin: 0 1px;
`

const Del = styled.span`
  background-color: #FADBDB;
  text-decoration: line-through;
  border-radius: 2px;
  padding: 1px 3px;
  margin: 0 1px;
  opacity: .8;
`

const Empty = styled.div`
  font-family: Hind;
  font-weight: 300;
  color: #999;
  padding: 30px 0;
`

const Controls = styled.div`
  display: flex;
  gap: 18px;
  margin-bottom: 10px;
`

const Absent = styled.span`
  color: #AAA;
  font-style: italic;
`

const Toggle = styled.div`
  font-family: Hind;
  font-size: 13px;
  color: #ECB595;
  cursor: pointer;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  border-top: 1px solid #EEE;
  padding-top: 16px;
  margin-top: 4px;
`

const FooterNote = styled.div`
  font-family: Hind;
  font-size: 13px;
  font-weight: 300;
  color: #777;
  margin-right: auto;
`

const Cancel = styled.div`
  font-family: Hind;
  font-size: 15px;
  font-weight: 300;
  color: #ECB595;
  cursor: pointer;
`

const Confirm = styled.button`
  font-family: Hind;
  font-size: 15px;
  color: #FFF;
  background-color: #7FA98B;
  border: none;
  border-radius: 3px;
  padding: 9px 22px;
  cursor: pointer;
  opacity: ${p => p.disabled ? 0.5 : 1};
`

const ACTOR_LABEL = {
  owner: 'Submitter',
  editor: 'Editor',
  public_link: 'Public link',
  system: 'System'
}

/**
 * STY-578: revision history for one submission.
 *
 * Rendered only from AdminSidebar, which is itself editor-only — but the server
 * refuses these endpoints to anyone without edit_posts regardless, so hiding the
 * link is presentation, not the control.
 */
export default class History extends Component {
  constructor (props) {
    super(props)
    this.state = {
      loading: false,
      error: null,
      revisions: [],
      asSubmittedId: null,
      asPublishedId: null,
      leftId: null,
      rightId: null,
      cache: {},
      // Keyed by revision id. A single shared error field let a failure for a
      // revision the user had already navigated away from blank the whole panel,
      // even when both currently selected revisions had loaded fine.
      detailErrors: {},
      // The revision most recently clicked. The next click pairs against THIS,
      // not against whichever side happens to be newest — see select().
      anchorId: null,
      changedOnly: true,
      applying: false,
      // Kept apart from `error`. That one means the history could not be
      // loaded and correctly hides the body; a failed APPLY must leave the
      // diff and the Accept button on screen, or the message telling the
      // editor to try again is the only thing left and there is nothing to
      // try it with.
      applyError: null,
      // 'markup' shows both columns with word-level highlighting; 'clean' shows
      // only B with none, for reading the shipped copy as prose. Word's
      // Display for Review has the same escape hatch and editors use it.
      view: 'markup'
    }
  }

  componentDidMount () {
    if (this.props.open) this.load()
  }

  componentDidUpdate (prev, prevState) {
    if (this.props.open && !prev.open) this.load()

    // Embedded, the modal belongs to the workspace, so the guard on our own
    // handleClose never reaches it -- Shell hands back bare children and the
    // backdrop stays live. Tell the owner instead, and it holds the door.
    if (prevState.applying !== this.state.applying && this.props.onBusy) {
      this.props.onBusy(this.state.applying)
    }
  }

  load () {
    const { submissionId } = this.props
    this.setState({ loading: true, error: null })

    wpAxios.get(`${config.siteUrl}/api/v2/submissions/${submissionId}/revisions`)
      .then((res) => {
        const data = get(res, 'data.data', {})
        const revisions = data.revisions || []

        // Default pair: what the vendor submitted against what shipped. Then
        // ordered so the older is always A — asSubmitted is normally older than
        // asPublished, but nothing guarantees it (a republish after a vendor
        // edit would invert them), and the panel must never show a reversed diff.
        // When the API names both ends, take them — including when they are the
        // same revision, which means the shipped text IS the submitted text.
        // Discarding asSubmittedId in that case and reaching for "the next
        // available row" would answer a question nobody asked (baseline vs
        // published) in place of the one they did.
        //
        // As the server computes them today the two cannot collide: asPublished
        // selects reason='published', every published row is written with
        // actor_kind 'editor', and asSubmitted excludes editors. But that
        // invariant lives in smp_blog and this fallback lives here, with nothing
        // enforcing it across the boundary — so honour the answer rather than
        // second-guess it.
        // Reviewing a specific suggestion pins the later side to it and the
        // earlier side to the newest revision that actually happened — the
        // state the submission is in right now, which is what the editor is
        // being asked to compare against.
        const reviewing = this.props.suggestionId || null
        const currentId = reviewing
          ? get(revisions.filter(r => r.reason !== 'ai_suggestion'), '[0].id', null)
          : null

        // The revision the suggestion is being compared against. Accept stays
        // available only while the Before side is still this one.
        const reviewBaseId = currentId

        const candidateB = reviewing || data.asPublishedId || get(revisions, '[0].id', null)
        const candidateA = reviewing
          ? currentId
          : (data.asSubmittedId
            ? data.asSubmittedId
            : get(revisions.filter(r => r.id !== candidateB), '[0].id', null))

        let left
        let right

        if (reviewing) {
          // Not sorted by id here. The suggestion is always the "after" side
          // regardless of which row happens to have the higher id, because the
          // question is "what would this change", not "what happened when".
          left = currentId
          right = reviewing
        } else {
          const pair = [candidateA, candidateB].filter(x => x !== null && x !== undefined).sort((a, b) => a - b)
          left = pair.length > 1 ? pair[0] : null
          right = pair[pair.length - 1] || null
        }

        this.setState({
          loading: false,
          revisions: revisions,
          asSubmittedId: data.asSubmittedId,
          asPublishedId: data.asPublishedId,
          leftId: left,
          rightId: right,
          // The Before side Accept is pinned to. Browsing to an older revision
          // during review changes leftId and disables Accept, because the diff
          // on screen would then hide everything between that revision and the
          // current one -- which the full-document apply would remove.
          reviewBaseId: reviewBaseId,
          // Reset alongside the pair it belongs to. load() runs again every time
          // the modal is reopened; leaving a previous session's anchor behind
          // meant the first click was compared against a revision that is no
          // longer displayed, so the comparison jumped somewhere the user had
          // not asked for.
          anchorId: null,
          // Same reasoning — a failure from a previous session should not
          // surface against a freshly loaded pair.
          detailErrors: {}
        }, () => {
          if (left) this.fetchRevision(left)
          if (right && right !== left) this.fetchRevision(right)
        })
      })
      .catch((err) => {
        const status = get(err, 'response.status')
        this.setState({
          loading: false,
          error: status === 403
            ? 'Revision history is available to editors only.'
            : 'Could not load revision history.'
        })
      })
  }

  fetchRevision (id) {
    if (this.state.cache[id]) return
    const { submissionId } = this.props

    wpAxios.get(`${config.siteUrl}/api/v2/submissions/${submissionId}/revisions/${id}`)
      .then((res) => {
        const rev = get(res, 'data.data')
        this.setState((prev) => {
          const detailErrors = Object.assign({}, prev.detailErrors)
          delete detailErrors[id]
          return { cache: Object.assign({}, prev.cache, { [id]: rev }), detailErrors: detailErrors }
        })
      })
      .catch(() => {
        this.setState(prev => ({
          detailErrors: Object.assign({}, prev.detailErrors, {
            [id]: 'Could not load this revision.'
          })
        }))
      })
  }

  select (id) {
    // Each click pairs the clicked revision with the PREVIOUSLY clicked one,
    // then orders the two so the earlier is always Before.
    //
    // Pairing against the current "after" instead — which is what this did —
    // pinned that side permanently. Once After was the newest revision, every
    // click only ever moved Before, so two older revisions could never be
    // compared with each other. Starting from 1 and 3: clicking 2 gave 2→3, and
    // clicking 1 then gave 1→3. 1→2 was unreachable, and so was every other
    // consecutive step through the older history — which is exactly the
    // question "what did this one editing pass change?".
    //
    // With an anchor, that same sequence gives 2→3 then 1→2: each click walks
    // the comparison one step back rather than dragging one end along.
    //
    // Ordering on id, not created_at: id is auto-increment and strictly
    // monotonic, while created_at is second-precision and ties routinely
    // (ensureBaseline writes immediately before snapshot).
    this.setState((prev) => {
      const against = prev.anchorId !== null ? prev.anchorId : prev.rightId
      if (id === against) return null

      const ordered = [against, id]
        .filter(x => x !== null && x !== undefined)
        .sort((a, b) => a - b)

      return {
        anchorId: id,
        leftId: ordered.length > 1 ? ordered[0] : null,
        rightId: ordered[ordered.length - 1]
      }
    }, () => this.fetchRevision(id))
  }

  apply () {
    const { onApply, handleClose, suggestionId, suggestionBase } = this.props

    // Keyed to what is DISPLAYED, not to the suggestion this was opened with.
    // The revision list stays clickable in review mode, so an editor can browse
    // to another comparison; applying the originally targeted payload would
    // then persist something other than the diff they were looking at when they
    // pressed the button.
    //
    // BOTH sides, not just the later one. Clicking an older revision sorts the
    // pair as (older -> suggestion), which left rightId untouched and Accept
    // live while the diff on screen no longer showed what the apply would
    // overwrite: everything between that older revision and the current one
    // was hidden, and applying the full document removes it.
    if (!this.showingSuggestion()) return

    const suggestion = this.state.cache[suggestionId]

    if (!suggestion || !onApply || this.state.applying) return

    // Applied through the container's save queue, not posted from here: that
    // queue serializes writes so accepting a suggestion cannot race an editor
    // who is typing in another field.
    this.setState({ applying: true, applyError: null })

    Promise.resolve(onApply(suggestion.data, { baseHash: suggestionBase }))
      .then(() => {
        this.setState({ applying: false })
        handleClose()
      })
      .catch((err) => {
        // Stay open. The save path reports its own failure via a snackbar, but
        // closing here would drop the suggestion the editor was reviewing and
        // leave them nothing to retry from, on a save that never happened.
        //
        // A 409 is the baseline check refusing: the submission moved while this
        // diff was on screen, so retrying the same payload would keep failing.
        // Its message says to regenerate instead.
        const conflict = get(err, 'response.status') === 409
        const message = get(err, 'response.data.message')

        this.setState({
          applying: false,
          // Kept apart from `error`. That one means the history could not be
          // loaded and correctly hides the body; a failed APPLY must leave the
          // diff and the Accept button on screen, or the message telling the
          // editor to try again is the only thing left and there is nothing to
          // try it with.
          applyError: null,
          error: (conflict && message)
            ? message
            : 'The suggestion was not applied — nothing has changed. You can try again.'
        })
      })
  }

  renderRow (r) {
    const selected = r.id === this.state.leftId || r.id === this.state.rightId
    const side = r.id === this.state.rightId ? 'after' : (r.id === this.state.leftId ? 'before' : '')

    return (
      <Row key={r.id} $selected={selected} onClick={() => this.select(r.id)}>
        <RowTop>
          <div>
            <Who $kind={r.actorKind}>{ACTOR_LABEL[r.actorKind] || r.actorKind}</Who>
            {r.id === this.state.asSubmittedId && <Tag $tone='submitted'>as submitted</Tag>}
            {r.reason === 'published' && <Tag $tone='published'>published</Tag>}
          </div>
          {side ? <SideMark $side={side}>{side}</SideMark> : <When />}
        </RowTop>
        <When>{moment(r.createdAt).format('MMM D, YYYY h:mm a')}</When>
      </Row>
    )
  }

  columnLabel (revisionId) {
    const meta = this.state.revisions.filter(r => r.id === revisionId)[0]
    if (!meta) return null

    return (
      <span>
        {ACTOR_LABEL[meta.actorKind] || meta.actorKind}
        {meta.reason === 'published' ? ' · published' : ''}
        {' · '}
        {moment(meta.createdAt).format('MMM D, h:mm a')}
      </span>
    )
  }

  renderRun (part, i, column) {
    // A run is highlighted only in the column it belongs to. Deletions are
    // marked on the left, insertions on the right; everything else is plain on
    // both sides, so each column reads as its own version of the text.
    if (column === 'a' && part.removed) return <Del key={i}>{part.value}</Del>
    if (column === 'b' && part.added) return <Ins key={i}>{part.value}</Ins>
    return <span key={i}>{part.value}</span>
  }

  // The one condition apply() enforces, so the button cannot look available
  // while the handler silently refuses. BOTH sides: the suggestion on the
  // right, and the Before side still the revision it was generated against.
  showingSuggestion () {
    const { suggestionId } = this.props

    return this.state.rightId === suggestionId && this.state.leftId === this.state.reviewBaseId
  }

  render () {
    const { open, handleClose, suggestionId } = this.props
    const { loading, error, revisions, leftId, rightId, cache, changedOnly, view, detailErrors, applying, applyError } = this.state

    // Only an error belonging to a revision that is still selected should be
    // shown; anything else is noise from a comparison the user has moved on from.
    const activeDetailError = detailErrors[leftId] || detailErrors[rightId] || null

    // Read from the same method apply() guards on, so the button's appearance
    // and the handler's behaviour cannot drift apart again.
    const showingSuggestion = this.showingSuggestion()

    const left = cache[leftId]
    const right = cache[rightId]

    // With only one revision recorded there is no earlier side to fetch, so
    // waiting for both would spin on "Loading revisions…" forever — the state
    // every submission is in immediately after this feature ships.
    const soloRevision = revisions.length === 1 || leftId === null
    const ready = soloRevision ? !!right : !!(left && right)
    const blocks = ready
      ? pairBlocks(soloRevision ? null : left.data, right.data)
      : []
    const clean = view === 'clean' || soloRevision

    // The changed-only filter is a diff affordance and does not apply to the
    // clean read. That mode exists to read the shipped piece as prose; filtering
    // it returns a document with holes, and on a revision pair where nothing
    // changed it returned "no text changed" instead of the copy itself.
    const shown = (changedOnly && !clean && !soloRevision) ? blocks.filter(b => b.changed) : blocks
    const changedCount = blocks.filter(b => b.changed).length

    return (
      <ErrorBoundary>
        {/* The modal's own dismissal is a close path too. An apply cannot be
            cancelled once sent, so backdrop and escape are held shut for the
            couple of seconds it is in flight; otherwise the dialog vanishes as
            though nothing happened while the write goes on to land. */}
        <Shell
          embedded={this.props.embedded}
          open={open}
          handleClose={applying ? () => {} : handleClose}
        >
          <Panel $embedded={this.props.embedded}>
            <Title>{suggestionId ? 'Review copy edits' : 'Revision history'}</Title>
            <Sub>
              {suggestionId ? (
                <span>
                  <strong>Before</strong> is the submission as it stands, <strong>After</strong> is
                  what the copy edit proposes. Only spelling, grammar and punctuation were
                  allowed to change; anything that altered wording was rejected before you saw it.
                </span>
              ) : (
                <span>
              <strong>Before</strong> is the earlier revision, <strong>After</strong> the later one —
              click any revision to add it to the comparison. History begins when this feature shipped;
              earlier edits were overwritten in place.
                </span>
              )}
            </Sub>

            {loading && <Empty>Loading…</Empty>}
            {error && <Empty>{error}</Empty>}
            {!error && activeDetailError && <Empty>{activeDetailError}</Empty>}

            {!loading && !error && !revisions.length && (
              <Empty>No revisions recorded yet for this submission.</Empty>
            )}

            {!loading && !error && revisions.length > 0 && (
              <Body>
                <List>{revisions.map(r => this.renderRow(r))}</List>
                <Diff>
                  <StickyTop>
                    <Controls>
                      {!clean && (
                        <Toggle onClick={() => this.setState({ changedOnly: !changedOnly })}>
                          {changedOnly ? `Show all fields` : `Show changed only (${changedCount})`}
                        </Toggle>
                      )}
                      <Toggle onClick={() => this.setState({ view: clean ? 'markup' : 'clean' })}>
                        {clean ? 'Show changes' : 'Read final copy without markup'}
                      </Toggle>
                    </Controls>

                    {soloRevision && ready && (
                      <Sub style={{ margin: '4px 0 0' }}>
                        Only one revision recorded so far — nothing to compare it against yet.
                      </Sub>
                    )}

                    {ready && shown.length > 0 && !clean && !soloRevision && (
                      <ColumnHead>
                        <ColumnLabel $side='before'><strong>Before</strong> · {this.columnLabel(leftId)}</ColumnLabel>
                        <ColumnLabel $side='after'><strong>After</strong> · {this.columnLabel(rightId)}</ColumnLabel>
                      </ColumnHead>
                    )}
                  </StickyTop>

                  {!ready && <Empty>Loading revisions…</Empty>}

                  {ready && !shown.length && (
                    <Empty>
                      {clean
                        ? 'This revision contains no text.'
                        : (changedOnly
                          ? 'No text changed between these two revisions.'
                          : 'These revisions contain no text.')}
                    </Empty>
                  )}

                  {ready && shown.map((block) => {
                    const split = sides(block.parts)
                    // A field can be empty on both sides — an untouched custom
                    // field, or Quill's "<p><br></p>" empty sentinel, which
                    // normalises to nothing. Saying "not in the earlier
                    // revision" AND "removed in the later revision" for the same
                    // field claims it was both added and deleted, when in truth
                    // it was simply never filled in.
                    const emptyBoth = !split.before.length && !split.after.length

                    return (
                      <Field key={block.key}>
                        {clean ? (
                          <div>
                            <FieldName>{block.label}</FieldName>
                            <Prose>{split.after.map(p => p.value).join('')}</Prose>
                          </div>
                        ) : (
                          // The field name is repeated over each column rather than
                          // spanning both. At ~450px per column with a gap between
                          // them, a single centred-ish heading leaves the right-hand
                          // column with no label in view, and once you have scrolled
                          // into the middle of a long field there is nothing above
                          // the right column saying what you are reading. Repeating
                          // costs a line and makes each column self-describing,
                          // which is the same reason each column carries its own
                          // full prose rather than only its changes.
                          <Columns>
                            <Cell $side='a'>
                              <FieldName>{block.label}</FieldName>
                              <Prose>
                                {split.before.length
                                  ? split.before.map((p, i) => this.renderRun(p, i, 'a'))
                                  : <Absent>{emptyBoth ? 'empty in both revisions' : 'not in the earlier revision'}</Absent>}
                              </Prose>
                            </Cell>
                            <Cell $side='b'>
                              <FieldName>{block.label}</FieldName>
                              <Prose>
                                {split.after.length
                                  ? split.after.map((p, i) => this.renderRun(p, i, 'b'))
                                  : <Absent>{emptyBoth ? 'empty in both revisions' : 'removed in the later revision'}</Absent>}
                              </Prose>
                            </Cell>
                          </Columns>
                        )}
                      </Field>
                    )
                  })}
                </Diff>
              </Body>
            )}

            {!loading && !error && suggestionId && (
              <Footer>
                <FooterNote>
                  {applyError
                    ? <span style={{ color: '#B4776F' }}>{applyError}</span>
                    : !showingSuggestion
                      ? 'Select the proposed revision again to accept it.'
                      : 'Accepting records the change as yours. Declining leaves the suggestion in the history without applying it.'}
                </FooterNote>
                {/* Decline closes the modal, but the apply already in flight is
                    not cancellable — clicking it mid-save dismissed the dialog
                    as though the suggestion had been rejected while the write
                    went on to persist it. */}
                <Cancel
                  style={applying ? { opacity: 0.5, cursor: 'default' } : undefined}
                  onClick={() => { if (!applying) handleClose() }}
                >
                  Decline
                </Cancel>
                <Confirm
                  disabled={!ready || applying || !showingSuggestion}
                  onClick={() => this.apply()}
                >
                  {applying
                    ? 'Applying…'
                    : (showingSuggestion ? 'Accept changes' : 'Showing another revision')}
                </Confirm>
              </Footer>
            )}
          </Panel>
        </Shell>
      </ErrorBoundary>
    )
  }
}
